import { Button as ButtonC, Heading, HStack, Spacer, useToast, VStack } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Location, useLocation, useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { Button } from '../../../components/atoms/Button';
import { Input } from '../../../components/atoms/Input';
import { Text } from '../../../components/atoms/Text';
import { useTheme } from '../../../contexts/ThemeProvider';

import { ShowPasswordToggle } from '@/components/atoms/ShowPasswordToggle';
import { SectionTitle } from '@/components/atoms/Title';
import { useProfile } from '@/contexts/ProfileProvider';
import { useLoginMutation } from '@/service/auth/hooks';
import { masks } from '@/utils/masks';
import { validarCNPJ, validarCPF } from '@/utils/validateDocuments';
import { Footer } from '../../../components/organisms/Footer';

export function LoginForm({ setExClient }: { setExClient?: (value: boolean) => void }) {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast({ isClosable: true });
  const { name, idp, fetchBenefits, exClientModule, integratedLogin, id, colors } = useTheme();
  const { updateProfile } = useProfile();
  const [showPassword, setShowPassword] = useState(false);

  const from = (location.state as { from?: Location } | null)?.from;

  const formSchema = z.object({
    username: z.string(),
    password: integratedLogin !== 'medlar' && integratedLogin !== 'surf' ? z.string().min(1) : z.string().optional(),
  });

  const methods = useForm({
    resolver: zodResolver(formSchema),
  });

  const { control, handleSubmit } = methods;

  const loginMutation = useLoginMutation({
    agreementId: id ? id : undefined,
    integratedLogin: integratedLogin ? integratedLogin : undefined,
    onSuccess() {
      updateProfile({});
      navigate(from ? `${from.pathname}${from.search}` : '/', { replace: true });
    },
    onError(error) {
      toast({ title: 'Oops!', description: error, status: 'error' });
    },
  });

  const isEmail = (value: string) => /\S+@\S+\.\S+/.test(value);

  const message = integratedLogin
    ? 'Para acessar a plataforma, utilize o mesmo login e senha do site'
    : 'Faça login com um CPF, Email ou CNPJ vinculado a sua conta';

  return (
    <VStack w={'100%'} align={'flex-start'} spacing={4}>
      <SectionTitle display={'flex'} gap={1} flexDir="row" fontSize={14} fontWeight={600}>
        <Text fontSize={14}>
          {message}
          <span
            style={{
              color: colors.primaryColor,
            }}
          >{` ${name}`}</span>
        </Text>
      </SectionTitle>
      <Heading fontWeight={700} letterSpacing={'-0.045em'} fontFamily={'Glory'} fontSize={'36px'}>
        Login
      </Heading>
      <form
        onSubmit={handleSubmit(({ username, password }) => {
          username = username.trim();

          if (integratedLogin) {
            loginMutation.mutate({ username, password, idp });
          } else if (isEmail(username) && id) {
            loginMutation.mutate({ username, password });
          } else if ((validarCPF(username) || validarCNPJ(username)) && id) {
            loginMutation.mutate({ username: masks.unmask(username), password });
          } else {
            toast({ title: 'Erro', description: 'Por favor, insira um email ou CPF/CNPJ válido.', status: 'error' });
          }
        })}
        style={{ width: '100%' }}
      >
        <VStack w={'100%'} spacing={7}>
          <VStack w={'100%'} spacing={4}>
            <Input
              control={control}
              name="username"
              label={integratedLogin ? 'Usuário' : undefined}
              login={integratedLogin ? false : true}
              autoComplete="off"
            />
            {integratedLogin !== 'medlar' && integratedLogin !== 'surf' && (
              <Input
                control={control}
                name="password"
                label="Senha"
                type={showPassword ? 'text' : 'password'}
                rightElement={<ShowPasswordToggle value={showPassword} onChange={setShowPassword} />}
                autoComplete="off"
              />
            )}
          </VStack>
          <ButtonC
            alignSelf={'flex-end'}
            variant={'link'}
            onClick={() => {
              if (integratedLogin) {
                toast({
                  title: 'Oops!',
                  description: 'Por favor, entre em contato com o suporte para recuperar sua senha.',
                  status: 'error',
                });
              } else {
                window.open(`https://api.edge.partiu.com.br/password_reset/?agreement_id=${id}`, '_blank');
              }
            }}
            color={colors.primaryColor}
          >
            <Heading fontWeight={500} fontSize={'16px'}>
              Esqueci minha senha
            </Heading>
          </ButtonC>

          <HStack w={'100%'} gap={3}>
            <Button
              type="button"
              onClick={() => navigate('/parcerias')}
              flex={1}
              backgroundColor="transparent"
              borderColor={colors.primaryColor}
              borderWidth={1}
              color={colors.primaryColor}
            >
              SEJA PARCEIRO
            </Button>
            <Button type="submit" isLoading={loginMutation.isLoading} flex={1}>
              ENTRAR
            </Button>
          </HStack>
          {exClientModule && (
            <Button
              backgroundColor="transparent"
              borderColor={colors.primaryColor}
              borderWidth={1}
              color={colors.primaryColor}
              onClick={() => setExClient && setExClient(true)}
              isLoading={loginMutation.isLoading}
            >
              Entrar como Ex-Cliente ?
            </Button>
          )}
          <Text
            color="#747474"
            margin="auto"
            textAlign={'center'}
            alignItems={'center'}
            gap={1}
            fontSize={14}
            fontWeight={400}
          >
            Está com dificuldade?{' '}
            <span onClick={() => navigate('/contato')} style={{ cursor: 'pointer', color: colors.primaryColor }}>
              Clique aqui
            </span>
            , entre em contato conosco.
          </Text>
        </VStack>
      </form>
      <Footer />
      <Spacer
        marginBottom={{
          base: '50px',
          md: '0px',
        }}
      />
    </VStack>
  );
}
